import axiosInstance from './axiosConfig';

// Withholding tax module — /api/client/withholding-tax (accounting firms only).
// docs/WHT_IMPLEMENTATION.md. Money is integer centavos, rates basis points.
//
// Every failure throws an Error carrying the server's `code`, the HTTP
// `status` and, for a rejected batch, `lineErrors` ([{ line, fields }], the
// BUG-2b shape that utils/encodingGrid.js rowErrorsFromLineErrors renders).
//
// Mutations must be called through useGuardedMutation so the axios
// interceptor stamps an Idempotency-Key on the POST/PUT.

const base = (clientId) => `/client/withholding-tax/${clientId}`;

function toApiError(error, fallback) {
  const data = error?.response?.data || {};
  return Object.assign(new Error(data.message || error?.message || fallback), {
    code: data.code || null,
    status: error?.response?.status ?? null,
    lineErrors: Array.isArray(data.errors) ? data.errors : [],
    response: error?.response,
  });
}

const call = async (fn, fallback) => {
  try {
    const response = await fn();
    return response.data;
  } catch (error) {
    throw toApiError(error, fallback);
  }
};

const withholdingTaxAPI = {
  /** Payee picker: the client's vendors + the tenant's unassigned ones (`unassigned: true`). */
  searchPayees: (clientId, q = '') =>
    call(() => axiosInstance.get(`${base(clientId)}/payees`, { params: q ? { q } : {} }), 'Failed to load payees'),

  /** ATCs allowed on 1601-EQ for this payee type + client scope on `date` (ISO, default today). */
  getAtcOptions: (clientId, { payeeType, date } = {}) =>
    call(() => axiosInstance.get(`${base(clientId)}/atc-options`, {
      params: { payeeType, ...(date && { date }) },
    }), 'Failed to load ATC options'),

  /** EWT rows for a year, optionally one month or one quarter. → { success, data } */
  listRecords: (clientId, { year, month, quarter } = {}) =>
    call(() => axiosInstance.get(`${base(clientId)}/records`, {
      params: { year, ...(month && { month }), ...(quarter && { quarter }) },
    }), 'Failed to load withholding records'),

  /**
   * All-or-nothing batch save. rows: [{ clientRowId, month, counterparty,
   * atcCode, rateBasisPoints?, baseCentavos, description?, invoiceNo?,
   * coaAccount?, glMode?, creditAccount? }]. Tax withheld is computed by the
   * server. → { success, data (rows echo clientRowId), warnings }
   */
  saveRecords: (clientId, { year, rows }) =>
    call(() => axiosInstance.post(`${base(clientId)}/records/batch`, { year, rows }), 'Failed to save withholding records'),

  /** Edit one row (same fields as a batch row). → { success, data, warnings } */
  updateRecord: (clientId, recordId, patch) =>
    call(() => axiosInstance.put(`${base(clientId)}/records/${recordId}`, patch), 'Failed to update the withholding record'),

  deleteRecord: (clientId, recordId) =>
    call(() => axiosInstance.delete(`${base(clientId)}/records/${recordId}`), 'Failed to delete the withholding record'),

  // ── BIR Form 0619-E (F5). Money in centavos; items keyed by BIR item number.

  /** Per-month status for a year → { success, data: { year, months: [...] } } */
  get0619eYear: (clientId, year) =>
    call(() => axiosInstance.get(`${base(clientId)}/returns/0619e/${year}`), 'Failed to load 0619-E statuses'),

  /** Computed (draft) or snapshotted (filed) view of one month. */
  get0619e: (clientId, year, month) =>
    call(() => axiosInstance.get(`${base(clientId)}/returns/0619e/${year}/${month}`), 'Failed to load the 0619-E'),

  /** Save the draft: { payment[], penaltyAuto, manualSurchargeCentavos, manualInterestCentavos, manualCompromiseCentavos } */
  save0619eDraft: (clientId, year, month, draft) =>
    call(() => axiosInstance.put(`${base(clientId)}/returns/0619e/${year}/${month}`, draft), 'Failed to save the 0619-E draft'),

  /** File it: { filedAt } (ISO instant). The filed version is read-only. */
  file0619e: (clientId, year, month, filedAt) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/0619e/${year}/${month}/file`, { filedAt }), 'Failed to file the 0619-E'),

  /** Start an amended version of the filed return. */
  amend0619e: (clientId, year, month) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/0619e/${year}/${month}/amend`, {}), 'Failed to amend the 0619-E'),

  // ── BIR Form 1601-EQ (F6). A rejected filing may carry `response.data.rows`
  // (MISSING_PAYEE_TIN: the EWT rows whose payee has no TIN).

  /** Per-quarter status for a year → { success, data: { year, quarters: [...] } } */
  get1601eqYear: (clientId, year) =>
    call(() => axiosInstance.get(`${base(clientId)}/returns/1601eq/${year}`), 'Failed to load 1601-EQ statuses'),

  get1601eq: (clientId, year, quarter) =>
    call(() => axiosInstance.get(`${base(clientId)}/returns/1601eq/${year}/${quarter}`), 'Failed to load the 1601-EQ'),

  /** { payment[], penaltyAuto, manual*Centavos, manualItems: { 20?, 21?, 24? } (null = computed), refundOption } */
  save1601eqDraft: (clientId, year, quarter, draft) =>
    call(() => axiosInstance.put(`${base(clientId)}/returns/1601eq/${year}/${quarter}`, draft), 'Failed to save the 1601-EQ draft'),

  file1601eq: (clientId, year, quarter, filedAt) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/1601eq/${year}/${quarter}/file`, { filedAt }), 'Failed to file the 1601-EQ'),

  amend1601eq: (clientId, year, quarter) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/1601eq/${year}/${quarter}/amend`, {}), 'Failed to amend the 1601-EQ'),

  // ── QAP DAT (DAT-B) — data + pre-export checks; the file is built by utils/qapDat.js ──

  /** → { success, data: { header, details[], control, return1601eq, checks[], ok, ... } } */
  getQap: (clientId, year, quarter) =>
    call(() => axiosInstance.get(`${base(clientId)}/qap/${year}/${quarter}`), 'Failed to load the QAP data'),

  // ── BIR Form 1601-C (F7b) ──────────────────────────────────────────────

  /** Month statuses for a year: { year, months: [{ month, status, isAmended, adjustedDueDate, … }] } */
  get1601cYear: (clientId, year) =>
    call(() => axiosInstance.get(`${base(clientId)}/returns/1601c/${year}`), 'Failed to load 1601-C statuses'),

  /** Computed (draft) or snapshotted (filed) view of one month, with scheduleI and warnings. */
  get1601c: (clientId, year, month) =>
    call(() => axiosInstance.get(`${base(clientId)}/returns/1601c/${year}/${month}`), 'Failed to load the 1601-C'),

  /** { taxReliefAvailing, taxReliefSpecify, scheduleI[], otherRemittancesCentavos, otherRemittancesSpecify, payment[], penaltyAuto, manual… } */
  save1601cDraft: (clientId, year, month, draft) =>
    call(() => axiosInstance.put(`${base(clientId)}/returns/1601c/${year}/${month}`, draft), 'Failed to save the 1601-C draft'),

  file1601c: (clientId, year, month, filedAt) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/1601c/${year}/${month}/file`, { filedAt }), 'Failed to file the 1601-C'),

  amend1601c: (clientId, year, month) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/1601c/${year}/${month}/amend`, {}), 'Failed to amend the 1601-C'),

  // ── GL posting (G1) — explicit: preview, then post ─────────────────────

  /** { saved: { field: id|null }, resolved: { field: { _id, accountNumber, accountName, source } | null } } */
  getPostingAccounts: (clientId) =>
    call(() => axiosInstance.get(`${base(clientId)}/posting-accounts`), 'Failed to load posting accounts'),

  /** Partial: { cashAccount?, apAccount?, ewtPayableAccount?, compPayableAccount?, compDebitAccount?, penaltyExpenseAccount? } (null = use the default) */
  savePostingAccounts: (clientId, accounts) =>
    call(() => axiosInstance.put(`${base(clientId)}/posting-accounts`, accounts), 'Failed to save posting accounts'),

  /** No writes. → { entries[{ id, kind, date, description, lines[{ accountNumber, accountName, debitCentavos, creditCentavos }], totals }], totals, skipped[], errors[], warnings[] } */
  previewGl: (clientId, ids) =>
    call(() => axiosInstance.post(`${base(clientId)}/gl/preview`, { ids }), 'Failed to preview the GL entries'),

  /** → { posted[], skipped[{ id, reason, message }], errors[], warnings[] } */
  postGl: (clientId, ids) =>
    call(() => axiosInstance.post(`${base(clientId)}/gl/post`, { ids }), 'Failed to post to the GL'),

  /** All unposted rows of a month and kind ('EWT' | 'COMP'). */
  postGlMonth: (clientId, { year, month, kind }) =>
    call(() => axiosInstance.post(`${base(clientId)}/gl/post-month`, { year, month, kind }), 'Failed to post to the GL'),

  // ── Return payment posting (G2) — form: '0619e' | '1601eq' | '1601c'; returnId = the filed version ──

  previewReturnPayment: (clientId, form, returnId) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/${form}/${returnId}/gl/preview-payment`, {}), 'Failed to preview the payment entry'),

  postReturnPayment: (clientId, form, returnId) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/${form}/${returnId}/gl/post-payment`, {}), 'Failed to post the payment entry'),

  /** Admin only. */
  reverseReturnPayment: (clientId, form, returnId, reason) =>
    call(() => axiosInstance.post(`${base(clientId)}/returns/${form}/${returnId}/gl/reverse-payment`, { reason }), 'Failed to reverse the payment entry'),

  // ── Employees + compensation rows (F7a) ────────────────────────────────

  listEmployees: (clientId, { includeInactive = false } = {}) =>
    call(() => axiosInstance.get(`${base(clientId)}/employees`, { params: includeInactive ? { includeInactive: true } : {} }), 'Failed to load employees'),

  /** { lastName, firstName, middleName?, tin?, isMwe? } */
  createEmployee: (clientId, employee) =>
    call(() => axiosInstance.post(`${base(clientId)}/employees`, employee), 'Failed to save the employee'),

  updateEmployee: (clientId, employeeId, patch) =>
    call(() => axiosInstance.put(`${base(clientId)}/employees/${employeeId}`, patch), 'Failed to save the employee'),

  /** Deletes — or deactivates when the employee has compensation rows. → { data: { deleted, deactivated } } */
  deactivateEmployee: (clientId, employeeId) =>
    call(() => axiosInstance.delete(`${base(clientId)}/employees/${employeeId}`), 'Failed to deactivate the employee'),

  listCompRecords: (clientId, { year, month } = {}) =>
    call(() => axiosInstance.get(`${base(clientId)}/comp-records`, { params: { year, ...(month && { month }) } }), 'Failed to load compensation records'),

  /** All-or-nothing. rows: [{ clientRowId, employee, month, description?, coaAccount?, totalCompCentavos, …, taxWithheldCentavos }] */
  saveCompRecords: (clientId, { year, rows }) =>
    call(() => axiosInstance.post(`${base(clientId)}/comp-records/batch`, { year, rows }), 'Failed to save compensation records'),

  updateCompRecord: (clientId, recordId, patch) =>
    call(() => axiosInstance.put(`${base(clientId)}/comp-records/${recordId}`, patch), 'Failed to update the compensation record'),

  deleteCompRecord: (clientId, recordId) =>
    call(() => axiosInstance.delete(`${base(clientId)}/comp-records/${recordId}`), 'Failed to delete the compensation record'),

  /**
   * CSV import. dryRun (default true) previews: { rows, errors, warnings, employeesToCreate }.
   * dryRun false saves all-or-nothing (a rejected file throws with lineErrors).
   */
  importCompCsv: (clientId, csv, { dryRun = true, createMissingEmployees = false } = {}) =>
    call(() => axiosInstance.post(`${base(clientId)}/comp-records/import`, csv, {
      params: { dryRun: dryRun ? 'true' : 'false', createMissingEmployees: createMissingEmployees ? 'true' : 'false' },
      headers: { 'Content-Type': 'text/csv' },
    }), 'Failed to import the CSV'),
};

export default withholdingTaxAPI;
